if (!customElements.get('rr-gallery-room')) {
  class RrGalleryRoom extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedReady) return;
      this.isConnectedReady = true;
      this.abortController = new AbortController();
      window.requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      this.isConnectedReady = false;
      this.initialized = false;
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.infiniteObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.pauseAllVideos(true);
      if (this.layoutFrame) window.cancelAnimationFrame(this.layoutFrame);
      if (this.viewer?.open) {
        this.cleanupActiveViewerMedia();
        this.viewer.close();
        this.unlockPageScroll();
      }
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      this.initialized = true;
      this.grid = this.querySelector('[data-gr-grid]');
      this.items = Array.from(this.querySelectorAll('[data-gr-item]'));
      this.loadingBehavior = this.dataset.loadingBehavior || 'show_more';
      this.batchSize = Math.max(1, Number.parseInt(this.dataset.imagesPerLoad || '8', 10));
      this.loadControls = this.querySelector('[data-load-controls]');
      this.loadMoreButton = this.querySelector('[data-load-more]');
      this.sentinel = this.querySelector('[data-infinite-sentinel]');
      this.videos = Array.from(this.querySelectorAll('.gr__video'));

      this.items.forEach((item) => {
        if (item.dataset.initialHidden === 'true') item.hidden = true;
        item.removeAttribute('data-initial-hidden');
      });

      this.bindLoading();
      this.bindMasonry();
      this.bindVideoPreviews();
      this.bindThemeEditor();
      this.bindViewer();
      this.updateLoadControls();
      this.scheduleLayout();
    }

    bindLoading() {
      const signal = this.abortController.signal;

      this.loadMoreButton?.addEventListener('click', () => this.revealNext(), { signal });

      if (this.loadingBehavior === 'infinite' && this.sentinel) {
        this.infiniteObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.revealNext();
          },
          { rootMargin: '400px 0px' }
        );
        this.infiniteObserver.observe(this.sentinel);
      }
    }

    revealNext() {
      const hiddenItems = this.items.filter((item) => item.hidden);
      hiddenItems.slice(0, this.batchSize).forEach((item) => {
        item.hidden = false;
        item.classList.add('is-revealing');
        item.addEventListener('animationend', () => item.classList.remove('is-revealing'), { once: true });
      });
      this.updateLoadControls();
      this.observeVisibleItems();
      this.observeVisibleVideos();
      this.scheduleLayout();
    }

    updateLoadControls() {
      if (!this.loadControls) return;
      const complete = this.items.every((item) => !item.hidden);
      this.loadControls.hidden = complete;
      if (complete) this.infiniteObserver?.disconnect();
    }

    bindMasonry() {
      if (!this.grid || this.dataset.layout !== 'masonry') return;
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
      this.resizeObserver.observe(this.grid);
      this.observeVisibleItems();
      this.items.forEach((item) => {
        const media = item.querySelector('.gr__image, .gr__video');
        if (media instanceof HTMLImageElement && !media.complete) {
          media.addEventListener('load', () => this.scheduleLayout(), {
            once: true,
            signal: this.abortController.signal,
          });
        } else if (media instanceof HTMLVideoElement) {
          media.addEventListener('loadedmetadata', () => this.scheduleLayout(), {
            once: true,
            signal: this.abortController.signal,
          });
        }
      });
    }

    observeVisibleItems() {
      if (!this.resizeObserver) return;
      this.items.forEach((item) => {
        if (!item.hidden) {
          const content = item.querySelector('[data-gr-item-content]');
          if (content && !content.dataset.resizeObserved) {
            content.dataset.resizeObserved = 'true';
            this.resizeObserver.observe(content);
          }
        }
      });
    }

    bindVideoPreviews() {
      if (!this.videos.length) return;
      const signal = this.abortController.signal;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.35 && !this.viewer?.open) {
              this.startVideoPreview(video);
            } else {
              this.pauseVideoPreview(video, true);
            }
          });
        },
        { threshold: [0, 0.35, 0.75] }
      );

      this.videos.forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.dataset.previewVisible = 'false';
        video.addEventListener(
          'loadedmetadata',
          () => {
            this.configureVideoSegment(video);
            if (video.dataset.previewVisible === 'true') this.restartVideoSegment(video);
          },
          { signal }
        );
        video.addEventListener('durationchange', () => this.configureVideoSegment(video), { signal });
        video.addEventListener('timeupdate', () => this.enforceVideoSegment(video), { signal });
        video.addEventListener('ended', () => this.restartVideoSegment(video), { signal });
      });

      this.querySelectorAll('[data-audio-control]').forEach((button) => {
        button.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            const video = button.closest('[data-video-preview]')?.querySelector('.gr__video');
            if (!video) return;
            this.setVideoMuted(video, !video.muted);
            if (!video.muted) this.playVideo(video);
          },
          { signal }
        );
      });

      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.pauseAllVideos(true);
          } else {
            this.resumeVisibleVideos();
          }
        },
        { signal }
      );

      this.observeVisibleVideos();
    }

    observeVisibleVideos() {
      if (!this.videoObserver) return;
      this.items.forEach((item) => {
        const video = item.querySelector('.gr__video');
        if (!video || item.hidden || video.dataset.previewObserved === 'true') return;
        video.dataset.previewObserved = 'true';
        this.videoObserver.observe(video);
      });
    }

    startVideoPreview(video) {
      video.dataset.previewVisible = 'true';
      if (video.preload === 'none') {
        video.preload = 'metadata';
        video.load();
      }
      if (video.readyState >= 1) {
        this.configureVideoSegment(video);
        this.restartVideoSegment(video);
      }
    }

    configureVideoSegment(video) {
      const wrapper = video.closest('[data-video-preview]');
      if (!wrapper || !Number.isFinite(video.duration) || video.duration <= 0) return;
      const requestedStart = Math.max(0, Number.parseFloat(wrapper.dataset.previewStart || '0'));
      const requestedDuration = Math.min(7, Math.max(2, Number.parseFloat(wrapper.dataset.previewDuration || '4')));
      const usableDuration = Math.min(requestedDuration, video.duration);
      video.previewStart = requestedStart < video.duration ? requestedStart : Math.max(0, video.duration - usableDuration);
      video.previewEnd = Math.min(video.duration, video.previewStart + usableDuration);
    }

    restartVideoSegment(video) {
      if (video.dataset.previewVisible !== 'true' || this.viewer?.open) return;
      if (!Number.isFinite(video.previewStart)) this.configureVideoSegment(video);
      if (Number.isFinite(video.previewStart)) {
        try {
          video.currentTime = video.previewStart;
        } catch (_error) {
          return;
        }
      }
      this.playVideo(video);
    }

    enforceVideoSegment(video) {
      if (!Number.isFinite(video.previewEnd) || !Number.isFinite(video.previewStart)) return;
      if (video.currentTime >= video.previewEnd - 0.08 || video.currentTime < video.previewStart - 0.25) {
        this.restartVideoSegment(video);
      }
    }

    playVideo(video) {
      const playAttempt = video.play();
      if (playAttempt?.catch) playAttempt.catch(() => video.pause());
    }

    pauseVideoPreview(video, mute) {
      video.dataset.previewVisible = 'false';
      video.pause();
      if (mute) this.setVideoMuted(video, true);
    }

    pauseAllVideos(mute) {
      this.videos?.forEach((video) => {
        video.pause();
        if (mute) this.setVideoMuted(video, true);
      });
    }

    resumeVisibleVideos() {
      this.videos?.forEach((video) => {
        const bounds = video.getBoundingClientRect();
        const visibleHeight = Math.min(bounds.bottom, window.innerHeight) - Math.max(bounds.top, 0);
        if (visibleHeight > 0 && visibleHeight / Math.max(bounds.height, 1) >= 0.35) this.startVideoPreview(video);
      });
    }

    setVideoMuted(video, muted) {
      if (!muted && RrGalleryRoom.audibleVideo && RrGalleryRoom.audibleVideo !== video) {
        const previous = RrGalleryRoom.audibleVideo;
        previous.muted = true;
        previous.closest('[data-video-preview]')?.querySelector('[data-audio-control]')?.setAttribute('aria-pressed', 'false');
        previous.closest('[data-video-preview]')?.querySelector('[data-audio-control]')?.setAttribute('aria-label', 'Unmute video');
      }

      video.muted = muted;
      const button = video.closest('[data-video-preview]')?.querySelector('[data-audio-control]');
      button?.setAttribute('aria-pressed', String(!muted));
      button?.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      RrGalleryRoom.audibleVideo = muted ? (RrGalleryRoom.audibleVideo === video ? null : RrGalleryRoom.audibleVideo) : video;
    }

    scheduleLayout() {
      if (this.dataset.layout !== 'masonry') return;
      if (this.layoutFrame) window.cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => this.layoutMasonry());
    }

    layoutMasonry() {
      if (!this.grid) return;
      const gridStyles = window.getComputedStyle(this.grid);
      const rowSize = Number.parseFloat(gridStyles.gridAutoRows) || 1;
      const rowGap = Number.parseFloat(gridStyles.rowGap) || 0;

      this.items.forEach((item) => {
        if (item.hidden) return;
        const content = item.querySelector('[data-gr-item-content]');
        if (!content) return;
        const height = content.getBoundingClientRect().height;
        const rowSpan = Math.max(1, Math.ceil((height + rowGap) / (rowSize + rowGap)));
        item.style.gridRowEnd = `span ${rowSpan}`;
      });
    }

    bindThemeEditor() {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const selected = event.target.closest('[data-gr-item]');
          if (!selected) return;
          const selectedIndex = this.items.indexOf(selected);
          this.items.slice(0, selectedIndex + 1).forEach((item) => {
            item.hidden = false;
          });
          this.updateLoadControls();
          this.observeVisibleItems();
          this.observeVisibleVideos();
          this.scheduleLayout();
          selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        },
        { signal: this.abortController.signal }
      );
    }

    bindViewer() {
      this.viewer = this.querySelector('[data-viewer]');
      if (!this.viewer) return;

      this.viewerItems = this.items.filter((item) => item.dataset.viewerEligible === 'true');
      this.viewerStage = this.querySelector('[data-viewer-stage]');
      this.viewerMedia = this.querySelector('[data-viewer-media]');
      this.viewerMetadata = this.querySelector('[data-viewer-metadata]');
      this.viewerCounter = this.querySelector('[data-viewer-counter]');
      this.viewerLink = this.querySelector('[data-viewer-link]');
      this.magnifier = this.querySelector('[data-magnifier]');
      const signal = this.abortController.signal;

      this.addEventListener(
        'click',
        (event) => {
          const trigger = event.target.closest('[data-viewer-trigger]');
          if (!trigger || !this.contains(trigger)) return;
          const item = trigger.closest('[data-gr-item]');
          const index = this.viewerItems.indexOf(item);
          if (index >= 0) this.openViewer(index, trigger);
        },
        { signal }
      );

      this.querySelector('[data-viewer-close]')?.addEventListener('click', () => this.closeViewer(), { signal });
      this.querySelector('[data-viewer-previous]')?.addEventListener('click', () => this.showRelative(-1), { signal });
      this.querySelector('[data-viewer-next]')?.addEventListener('click', () => this.showRelative(1), { signal });

      this.viewer.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.closeViewer();
        },
        { signal }
      );

      this.viewer.addEventListener(
        'click',
        (event) => {
          if (event.target === this.viewer) this.closeViewer();
        },
        { signal }
      );

      this.viewer.addEventListener('close', () => this.afterViewerClose(), { signal });
      this.viewer.addEventListener('keydown', (event) => this.onViewerKeydown(event), { signal });
      this.bindMagnifier(signal);
      this.bindViewerSwipe(signal);
    }

    openViewer(index, opener) {
      if (!this.viewerItems.length || !this.viewer) return;
      this.viewerIndex = index;
      this.viewerOpener = opener;
      this.pauseAllVideos(true);
      this.lockPageScroll();
      if (!this.viewer.open) this.viewer.showModal();
      this.renderViewerItem();
      this.querySelector('[data-viewer-close]')?.focus();
    }

    closeViewer() {
      if (!this.viewer?.open) return;
      this.cleanupActiveViewerMedia();
      this.viewer.close();
    }

    afterViewerClose() {
      this.cleanupActiveViewerMedia();
      this.unlockPageScroll();
      this.resumeVisibleVideos();
      const opener = this.viewerOpener;
      this.viewerOpener = null;
      if (opener?.isConnected) opener.focus();
    }

    renderViewerItem() {
      const item = this.viewerItems[this.viewerIndex];
      const mediaTemplate = item?.querySelector('[data-gr-viewer-media]');
      if (!item || !mediaTemplate || !this.viewerMedia) return;

      this.cleanupActiveViewerMedia();
      const gridImage = item.querySelector('.gr__image');
      if (item.dataset.mediaType === 'gif' && gridImage) {
        this.sharedViewerImage = {
          image: gridImage,
          parent: gridImage.parentNode,
          nextSibling: gridImage.nextSibling,
          className: gridImage.className,
        };
        gridImage.className = 'gr__viewer-image gr__viewer-image--shared';
        this.viewerMedia.replaceChildren(gridImage);
      } else {
        this.viewerMedia.replaceChildren(mediaTemplate.content.cloneNode(true));
      }
      this.viewerMedia.dataset.mediaType = item.dataset.mediaType || 'image';

      const metadataTemplate = item.querySelector('[data-gr-metadata]');
      if (this.viewerMetadata) {
        this.viewerMetadata.replaceChildren();
        if (metadataTemplate) {
          const metadata = metadataTemplate.content.cloneNode(true);
          Array.from(metadata.querySelectorAll('[data-title], [data-caption], [data-year], [data-medium]')).forEach((field) => {
            if (!field.textContent.trim()) field.remove();
          });
          this.viewerMetadata.append(metadata);
        }
        this.viewerMetadata.hidden = !this.viewerMetadata.children.length;
      }

      if (this.viewerCounter) {
        const digits = Math.max(2, String(this.viewerItems.length).length);
        const current = String(this.viewerIndex + 1).padStart(digits, '0');
        const total = String(this.viewerItems.length).padStart(digits, '0');
        this.viewerCounter.textContent = `${current} / ${total}`;
      }

      const mediaLink = item.dataset.viewerLink;
      if (this.viewerLink && mediaLink) {
        this.viewerLink.hidden = false;
        this.viewerLink.href = mediaLink;
        if (item.dataset.viewerTarget === '_blank') {
          this.viewerLink.target = '_blank';
          this.viewerLink.rel = 'noopener noreferrer';
        } else {
          this.viewerLink.removeAttribute('target');
          this.viewerLink.removeAttribute('rel');
        }
      } else if (this.viewerLink) {
        this.viewerLink.hidden = true;
        this.viewerLink.removeAttribute('href');
        this.viewerLink.removeAttribute('target');
        this.viewerLink.removeAttribute('rel');
      }

      const hasMultiple = this.viewerItems.length > 1;
      this.querySelector('[data-viewer-previous]')?.toggleAttribute('hidden', !hasMultiple);
      this.querySelector('[data-viewer-next]')?.toggleAttribute('hidden', !hasMultiple);

      this.activeViewerVideo = this.viewerMedia.querySelector('.gr__viewer-video');
      if (this.activeViewerVideo) this.startViewerVideo(this.activeViewerVideo);
      this.activeMagnifierImage = this.viewerMedia.querySelector('[data-magnifier-eligible="true"]');
    }

    startViewerVideo(video) {
      video.loop = false;
      video.muted = false;
      video.volume = 1;
      const resetToStart = () => {
        try {
          video.currentTime = 0;
        } catch (_error) {
          return;
        }
      };
      resetToStart();
      if (video.readyState < 1) video.addEventListener('loadedmetadata', resetToStart, { once: true });
      const playAttempt = video.play();
      if (playAttempt?.catch) playAttempt.catch(() => {});
    }

    cleanupActiveViewerMedia() {
      this.hideMagnifier();
      const video = this.activeViewerVideo || this.viewerMedia?.querySelector('.gr__viewer-video');
      if (video) {
        video.pause();
        video.muted = true;
        try {
          video.currentTime = 0;
        } catch (_error) {
          video.removeAttribute('src');
        }
      }
      this.activeViewerVideo = null;
      this.activeMagnifierImage = null;
      if (this.sharedViewerImage) {
        const { image, parent, nextSibling, className } = this.sharedViewerImage;
        image.className = className;
        if (nextSibling?.parentNode === parent) {
          parent.insertBefore(image, nextSibling);
        } else {
          parent.append(image);
        }
        this.sharedViewerImage = null;
      }
      this.viewerMedia?.replaceChildren();
    }

    showRelative(offset) {
      if (this.viewerItems.length < 2) return;
      this.viewerIndex = (this.viewerIndex + offset + this.viewerItems.length) % this.viewerItems.length;
      this.renderViewerItem();
    }

    onViewerKeydown(event) {
      if (!this.viewer?.open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeViewer();
        return;
      }
      if (event.target instanceof HTMLVideoElement) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.showRelative(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.showRelative(1);
      }
    }

    bindViewerSwipe(signal) {
      if (!this.viewerStage) return;
      let touchStart = null;

      this.viewerStage.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1 || event.target.closest('video, button, a, input')) {
            touchStart = null;
            return;
          }
          touchStart = {
            x: event.touches[0].clientX,
            y: event.touches[0].clientY,
          };
        },
        { passive: true, signal }
      );

      this.viewerStage.addEventListener(
        'touchend',
        (event) => {
          if (this.magnifierTouchActive || Date.now() < (this.suppressViewerSwipeUntil || 0)) {
            touchStart = null;
            return;
          }
          if (!touchStart || event.changedTouches.length !== 1) return;
          const deltaX = event.changedTouches[0].clientX - touchStart.x;
          const deltaY = event.changedTouches[0].clientY - touchStart.y;
          touchStart = null;
          if (Math.abs(deltaX) < 56 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
          this.showRelative(deltaX > 0 ? -1 : 1);
        },
        { passive: true, signal }
      );
    }

    bindMagnifier(signal) {
      if (!this.magnifier || !this.viewerStage) return;
      let touchStart = null;
      let holdTimer = null;

      const clearHold = () => {
        if (holdTimer) window.clearTimeout(holdTimer);
        holdTimer = null;
      };

      signal.addEventListener(
        'abort',
        () => {
          clearHold();
          this.magnifierTouchActive = false;
        },
        { once: true }
      );

      this.viewerStage.addEventListener(
        'pointermove',
        (event) => {
          if (event.pointerType === 'touch' || !this.activeMagnifierImage) return;
          this.updateMagnifier(event.clientX, event.clientY);
        },
        { signal }
      );

      this.viewerStage.addEventListener(
        'pointerleave',
        (event) => {
          if (event.pointerType !== 'touch') this.hideMagnifier();
        },
        { signal }
      );

      this.viewerStage.addEventListener(
        'touchstart',
        (event) => {
          clearHold();
          if (!this.activeMagnifierImage || event.touches.length !== 1 || event.target.closest('video, button, a, input')) return;
          const touch = event.touches[0];
          if (!this.pointIsInsideImage(touch.clientX, touch.clientY)) return;
          touchStart = { x: touch.clientX, y: touch.clientY };
          holdTimer = window.setTimeout(() => {
            this.magnifierTouchActive = true;
            this.viewerStage.dataset.magnifierTouchActive = 'true';
            this.updateMagnifier(touchStart.x, touchStart.y);
          }, 325);
        },
        { passive: true, signal }
      );

      this.viewerStage.addEventListener(
        'touchmove',
        (event) => {
          if (!touchStart || event.touches.length !== 1) return;
          const touch = event.touches[0];
          if (!this.magnifierTouchActive) {
            if (Math.hypot(touch.clientX - touchStart.x, touch.clientY - touchStart.y) > 12) {
              clearHold();
              touchStart = null;
            }
            return;
          }
          event.preventDefault();
          this.updateMagnifier(touch.clientX, touch.clientY);
        },
        { passive: false, signal }
      );

      const endTouchMagnifier = (event) => {
        clearHold();
        if (this.magnifierTouchActive) {
          event.preventDefault();
          this.suppressViewerSwipeUntil = Date.now() + 250;
        }
        this.magnifierTouchActive = false;
        touchStart = null;
        this.viewerStage.removeAttribute('data-magnifier-touch-active');
        this.hideMagnifier();
      };

      this.viewerStage.addEventListener('touchend', endTouchMagnifier, { passive: false, signal });
      this.viewerStage.addEventListener('touchcancel', endTouchMagnifier, { passive: false, signal });
      this.viewerStage.addEventListener(
        'contextmenu',
        (event) => {
          if (this.activeMagnifierImage && this.pointIsInsideImage(event.clientX, event.clientY)) event.preventDefault();
        },
        { signal }
      );
      window.addEventListener('resize', () => this.hideMagnifier(), { signal });
    }

    pointIsInsideImage(clientX, clientY) {
      if (!this.activeMagnifierImage) return false;
      const rect = this.activeMagnifierImage.getBoundingClientRect();
      return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    }

    updateMagnifier(clientX, clientY) {
      const image = this.activeMagnifierImage;
      if (!image || !this.magnifier || !this.viewerStage) {
        this.hideMagnifier();
        return;
      }

      const imageRect = image.getBoundingClientRect();
      if (clientX < imageRect.left || clientX > imageRect.right || clientY < imageRect.top || clientY > imageRect.bottom) {
        this.hideMagnifier();
        return;
      }
      const stageRect = this.viewerStage.getBoundingClientRect();
      const styles = getComputedStyle(this);
      const configuredLensSize = Number.parseFloat(styles.getPropertyValue('--gr-magnifier-size')) || 180;
      const magnification = Number.parseFloat(styles.getPropertyValue('--gr-magnification')) || 2;
      const refractionStrength = this.classList.contains('gr--refractive-lens')
        ? Math.min(1, Math.max(0, Number.parseFloat(styles.getPropertyValue('--gr-refraction-strength')) || 0))
        : 0;
      const magnifiedWidth = Math.round(imageRect.width * magnification * 1000) / 1000;
      const magnifiedHeight = Math.round(imageRect.height * magnification * 1000) / 1000;
      const lensSize = Math.max(
        1,
        Math.min(configuredLensSize, magnifiedWidth, magnifiedHeight, stageRect.width, stageRect.height)
      );
      const stageX = clientX - stageRect.left;
      const stageY = clientY - stageRect.top;
      const sourceX = clientX - imageRect.left;
      const sourceY = clientY - imageRect.top;
      const minimumCursorX = Math.max(0, sourceX * magnification + lensSize - magnifiedWidth);
      const maximumCursorX = Math.min(lensSize, sourceX * magnification);
      const minimumCursorY = Math.max(0, sourceY * magnification + lensSize - magnifiedHeight);
      const maximumCursorY = Math.min(lensSize, sourceY * magnification);
      const cursorInLensX = Math.min(Math.max(lensSize / 2, minimumCursorX), maximumCursorX);
      const cursorInLensY = Math.min(Math.max(lensSize / 2, minimumCursorY), maximumCursorY);
      const lensLeft = Math.min(Math.max(0, stageX - cursorInLensX), Math.max(0, stageRect.width - lensSize));
      const lensTop = Math.min(Math.max(0, stageY - cursorInLensY), Math.max(0, stageRect.height - lensSize));
      const adjustedCursorX = stageX - lensLeft;
      const adjustedCursorY = stageY - lensTop;
      const backgroundX =
        Math.round(Math.min(0, Math.max(lensSize - magnifiedWidth, adjustedCursorX - sourceX * magnification)) * 1000) /
        1000;
      const backgroundY =
        Math.round(Math.min(0, Math.max(lensSize - magnifiedHeight, adjustedCursorY - sourceY * magnification)) * 1000) /
        1000;
      const source = image.dataset.magnifierSource || image.currentSrc || image.src;
      const safeSource = source.replaceAll('"', '\\"');

      if (this.magnifier.dataset.source !== source) {
        this.magnifier.style.backgroundImage = `url("${safeSource}")`;
        this.magnifier.style.setProperty('--gr-refraction-image', `url("${safeSource}")`);
        this.magnifier.dataset.source = source;
      }
      this.magnifier.style.width = `${lensSize}px`;
      this.magnifier.style.height = `${lensSize}px`;
      this.magnifier.style.backgroundSize = `${magnifiedWidth}px ${magnifiedHeight}px`;
      this.magnifier.style.backgroundPosition = `${backgroundX}px ${backgroundY}px`;
      if (refractionStrength > 0) {
        const refractedMagnification = magnification * (1 + refractionStrength * 0.16);
        const refractedWidth = Math.round(imageRect.width * refractedMagnification * 1000) / 1000;
        const refractedHeight = Math.round(imageRect.height * refractedMagnification * 1000) / 1000;
        const refractedX =
          Math.round(
            Math.min(0, Math.max(lensSize - refractedWidth, adjustedCursorX - sourceX * refractedMagnification)) * 1000
          ) / 1000;
        const refractedY =
          Math.round(
            Math.min(0, Math.max(lensSize - refractedHeight, adjustedCursorY - sourceY * refractedMagnification)) * 1000
          ) / 1000;
        this.magnifier.style.setProperty('--gr-refraction-size', `${refractedWidth}px ${refractedHeight}px`);
        this.magnifier.style.setProperty('--gr-refraction-position', `${refractedX}px ${refractedY}px`);
      }
      this.magnifier.style.transform = `translate3d(${lensLeft}px, ${lensTop}px, 0)`;
      this.viewerStage.dataset.magnifierActive = 'true';
    }

    hideMagnifier() {
      if (!this.magnifier || !this.viewerStage) return;
      this.viewerStage.removeAttribute('data-magnifier-active');
      this.magnifier.style.transform = 'translate3d(-200vw, -200vh, 0)';
    }

    lockPageScroll() {
      if (this.pageScrollState) return;
      const body = document.body;
      const scrollbarGap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const computedPadding = Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;
      this.pageScrollState = {
        x: window.scrollX,
        y: window.scrollY,
        position: body.style.position,
        top: body.style.top,
        left: body.style.left,
        right: body.style.right,
        width: body.style.width,
        overflow: body.style.overflow,
        paddingRight: body.style.paddingRight,
      };
      body.style.position = 'fixed';
      body.style.top = `-${this.pageScrollState.y}px`;
      body.style.left = `-${this.pageScrollState.x}px`;
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      if (scrollbarGap) body.style.paddingRight = `${computedPadding + scrollbarGap}px`;
    }

    unlockPageScroll() {
      if (!this.pageScrollState) return;
      const body = document.body;
      const state = this.pageScrollState;
      body.style.position = state.position;
      body.style.top = state.top;
      body.style.left = state.left;
      body.style.right = state.right;
      body.style.width = state.width;
      body.style.overflow = state.overflow;
      body.style.paddingRight = state.paddingRight;
      this.pageScrollState = null;
      window.scrollTo(state.x, state.y);
    }
  }

  customElements.define('rr-gallery-room', RrGalleryRoom);
}
